import React from 'react';
import { Heart, Users, Shield, Sparkles, HeartHandshake, Eye } from 'lucide-react';

const iconMap = {
  heart: Heart,
  users: Users,
  shield: Shield,
  sparkles: Sparkles,
  heartHandshake: HeartHandshake,
  eye: Eye,
};

const gradientMap = {
  heart: 'from-red-900/60 via-amber-900/40 to-black',
  users: 'from-amber-900/50 via-yellow-900/30 to-black',
  shield: 'from-emerald-900/40 via-amber-900/30 to-black',
  sparkles: 'from-purple-900/40 via-amber-900/30 to-black',
  heartHandshake: 'from-rose-900/50 via-amber-900/30 to-black',
  eye: 'from-indigo-900/40 via-amber-900/30 to-black',
};

const accentMap = {
  heart: 'text-red-400',
  users: 'text-amber-400',
  shield: 'text-emerald-400',
  sparkles: 'text-purple-400',
  heartHandshake: 'text-rose-400',
  eye: 'text-indigo-400',
};

const glowMap = {
  heart: 'shadow-red-500/20',
  users: 'shadow-amber-500/20',
  shield: 'shadow-emerald-500/20',
  sparkles: 'shadow-purple-500/20',
  heartHandshake: 'shadow-rose-500/20',
  eye: 'shadow-indigo-500/20',
};

export const BlogIconBanner = ({ icon = 'heart', size = 'large', title = '' }) => {
  const IconComponent = iconMap[icon] || Heart;
  const gradient = gradientMap[icon] || gradientMap.heart;
  const accent = accentMap[icon] || accentMap.heart;
  const glow = glowMap[icon] || glowMap.heart;

  if (size === 'small') {
    return (
      <div className={`w-full h-48 bg-gradient-to-br ${gradient} border-b border-amber-600/20 flex items-center justify-center relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-2 left-4"><IconComponent size={20} className="text-amber-400" /></div>
          <div className="absolute top-8 right-8"><IconComponent size={14} className="text-amber-400" /></div>
          <div className="absolute bottom-4 left-12"><IconComponent size={16} className="text-amber-400" /></div>
          <div className="absolute bottom-6 right-4"><IconComponent size={22} className="text-amber-400" /></div>
        </div>
        <div className={`w-16 h-16 rounded-2xl bg-black/50 border border-amber-500/30 flex items-center justify-center shadow-xl ${glow}`}>
          <IconComponent size={32} className={`${accent}`} strokeWidth={1.5} />
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-64 md:h-80 bg-gradient-to-br ${gradient} rounded-xl border border-amber-600/20 flex flex-col items-center justify-center relative overflow-hidden mb-8`}>
      {/* Decorative floating icons */}
      <div className="absolute inset-0 opacity-[0.04]">
        <div className="absolute top-6 left-8"><IconComponent size={40} className="text-amber-400" /></div>
        <div className="absolute top-12 right-16"><IconComponent size={28} className="text-amber-400" /></div>
        <div className="absolute bottom-8 left-20"><IconComponent size={32} className="text-amber-400" /></div>
        <div className="absolute bottom-12 right-10"><IconComponent size={44} className="text-amber-400" /></div>
        <div className="absolute top-1/2 left-6"><IconComponent size={24} className="text-amber-400" /></div>
        <div className="absolute top-8 left-1/3"><IconComponent size={20} className="text-amber-400" /></div>
      </div>

      {/* Central icon */}
      <div className={`w-24 h-24 rounded-3xl bg-black/60 border border-amber-500/40 flex items-center justify-center shadow-2xl ${glow} mb-5 backdrop-blur-sm`}>
        <IconComponent size={48} className={`${accent}`} strokeWidth={1.5} />
      </div>

      {/* Decorative line */}
      <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />
    </div>
  );
};

export const BlogIconCard = ({ icon = 'heart' }) => {
  const IconComponent = iconMap[icon] || Heart;
  const accent = accentMap[icon] || accentMap.heart;
  const glow = glowMap[icon] || glowMap.heart;

  return (
    <div className={`w-full h-32 bg-gradient-to-br from-zinc-900/80 to-black flex items-center justify-center relative overflow-hidden`}>
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute top-2 left-4"><IconComponent size={18} className="text-amber-400" /></div>
        <div className="absolute bottom-3 right-5"><IconComponent size={22} className="text-amber-400" /></div>
      </div>
      <div className={`w-14 h-14 rounded-2xl bg-black/60 border border-amber-500/30 flex items-center justify-center shadow-lg ${glow}`}>
        <IconComponent size={28} className={`${accent}`} strokeWidth={1.5} />
      </div>
    </div>
  );
};

export default BlogIconBanner;
